import { Box, Toolbar, Snackbar, Alert, Typography, Button, Chip } from '@mui/material';
import { Outlet } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import { hideSnackbar } from '../../features/ui/uiSlice';
import { ExitToApp, SupervisorAccount } from '@mui/icons-material';

/* ── Impersonation Exit Banner ─────────────────────────────────── */
function ImpersonationBanner() {
  const { user } = useSelector(s => s.auth);
  const [backup, setBackup] = useState(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem('pos_auth_superadmin_backup');
      if (raw) setBackup(JSON.parse(raw));
    } catch { /* ignore */ }
  }, []);

  if (!backup) return null;

  const exitImpersonation = () => {
    // Restore the super admin token and do a hard reload so Redux state re-initialises from localStorage
    localStorage.setItem('pos_auth', JSON.stringify(backup));
    localStorage.removeItem('pos_auth_superadmin_backup');
    window.location.href = '/superadmin';
  };

  return (
    <Box sx={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      bgcolor: '#7c3aed',
      color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      px: 3, py: 0.75,
      boxShadow: '0 2px 8px rgba(124,58,237,0.4)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <SupervisorAccount sx={{ fontSize: 18 }} />
        <Typography variant="body2" fontWeight={600}>
          Super Admin — Impersonating:
        </Typography>
        <Chip
          label={user?.name || 'Tenant Owner'}
          size="small"
          sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 700, fontSize: 12 }}
        />
        <Typography variant="caption" sx={{ opacity: 0.8 }}>
          (Any actions taken will affect this tenant's data)
        </Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        startIcon={<ExitToApp sx={{ fontSize: 16 }} />}
        onClick={exitImpersonation}
        sx={{
          color: 'white', borderColor: 'rgba(255,255,255,0.5)',
          '&:hover': { bgcolor: 'rgba(255,255,255,0.15)', borderColor: 'white' },
          fontWeight: 700, fontSize: 12,
        }}
      >
        Exit Impersonation
      </Button>
    </Box>
  );
}

/* ── Main Layout ────────────────────────────────────────────────── */
export default function MainLayout() {
  const { sidebarOpen, snackbar } = useSelector(s => s.ui);
  const dispatch = useDispatch();

  // Check if we are in impersonation mode
  const isImpersonating = !!localStorage.getItem('pos_auth_superadmin_backup');
  const BANNER_H = isImpersonating ? 38 : 0;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <ImpersonationBanner />
      <Topbar impersonationOffset={BANNER_H} />
      <Sidebar open={sidebarOpen} topOffset={BANNER_H} />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: 'margin 0.3s ease',
          minHeight: '100vh',
          bgcolor: 'background.default',
          overflow: 'hidden',
          mt: `${BANNER_H}px`,
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3 }}>
          <Outlet />
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => dispatch(hideSnackbar())} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
